/**
 * useApiClient Hook
 * Custom hook for handling API calls (POST, PUT, PATCH, DELETE) with loading and error states
 * This hook should be used for mutation operations, not for GET requests (use Redux thunks for those)
 */

import { useState, useCallback } from 'react';
import { getApiErrorMessage } from '../utils/api/ApiClient';
import { toast } from '../utils/toast';

interface UseApiClientOptions {
    toast?: boolean;
}

interface UseApiClientReturn {
    loading: boolean;
    error: string | null;
    callApi: <T>(
        apiCall: () => Promise<T>,
        onSuccess?: (data: T) => void,
        onError?: (error: string) => void
    ) => Promise<T | null>;
    setError: (error: string | null) => void;
    clearError: () => void;
}

/**
 * Custom hook for API calls with automatic loading and error state management
 */
export function useApiClient(options?: UseApiClientOptions): UseApiClientReturn {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const showToast = options?.toast ?? false;

    const callApi = useCallback(
        async <T,>(
            apiCall: () => Promise<T>,
            onSuccess?: (data: T) => void,
            onError?: (error: string) => void
        ): Promise<T | null> => {
            try {
                setLoading(true);
                setError(null);

                const result = await apiCall();

                setLoading(false);

                if (onSuccess) {
                    onSuccess(result);
                }

                return result;
            } catch (err: unknown) {
                const errorMessage = getApiErrorMessage(err);
                setError(errorMessage);
                setLoading(false);

                if (showToast) {
                    toast.error(errorMessage);
                }

                if (onError) {
                    onError(errorMessage);
                }

                if (import.meta.env.DEV) {
                    console.error('[useApiClient Error]', err);
                }

                return null;
            }
        },
        [showToast]
    );

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        loading,
        error,
        callApi,
        setError,
        clearError,
    };
}
